import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import httpClient from "../../lib/httpClient";
import { Grid, Box, Card } from "@material-ui/core";
import {
  tableCellRender,
  dateFilterOperators,
  stringFilterOperators,
  formatDateValue,
  getLatestUpdateAgro,
  filterModelToQueryValue,
  getCompanyCodeName,
  isSingleSelectOperators,
  getCompanyCodeFilter,
} from "../util/tableUtils";

import { UniversalMap } from "../map/UniversalMap";
import { UpdateMapButton } from "../util/UpdateMapButton";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import { saveUserMonitorData } from "../util/userUtils";
import { useClientConfig } from "../../context/clientConfigContext";

export const AgricultureClaimsList = () => {
  const { t } = useTranslation();
  const config = useClientConfig();

  const [agricultureClaimsData, setAgricultureClaimsData] = useState([]);
  const [tableState, setTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [filterModel, setFilterModel] = useState({});
  const [sortModel, setSortModel] = useState({});
  const [filteredMapData, setFilteredMapData] = useState(null);
  const [lastUpdateDateMsg, setLastUpdateDateMsg] = useState("");

  const onFilterChange = useCallback((aFilterModel) => {
    setFilterModel(aFilterModel);
  }, []);

  const onSortChange = useCallback((aSortModel) => {
    setSortModel(aSortModel);
  }, []);

  const onTableRowClick = (rowData) => {
    const policyNumber = rowData.row.policy.policyNumber;
    const complainId = rowData.row.complainID;
    let wnd = window.open(`#/claimsagriculture/${policyNumber}/${complainId}`, "_blank");
    wnd.onload = function () {
      wnd.document.title = `${complainId}`;
    };
  };

  const getPoliciesData = useCallback(async () => {
    setTableState((old) => ({ ...old, isLoading: true }));
    let sortField, sortDirection;
    const sortItem = sortModel;
    if (sortItem && Object.keys(sortItem).length) {
      const { field, sort } = sortItem[0];
      sortField = field;
      sortDirection = sort;
    }

    const request = `/agricultureClaims?page=${paginationModel.page}&pageSize=${
      paginationModel.pageSize
    }&filters=${filterModelToQueryValue(filterModel)}&orderBy=${sortField}&orderDirection=${sortDirection}`;

    const { data: claimsResponse } = await httpClient().get(request);

    setAgricultureClaimsData(claimsResponse.data);
    setTableState((old) => ({
      ...old,
      isLoading: false,
      total: claimsResponse.total,
    }));
  }, [filterModel, sortModel, paginationModel]);

  useEffect(() => {
    getLatestUpdateAgro("/serviceLogs/agriculture?service=agricultureDb_sinistri").then((lastUpdateMsg) => {
      setLastUpdateDateMsg(lastUpdateMsg);
    });
    saveUserMonitorData("agricultureClaimsList", "");
  }, []);

  useEffect(() => {
    if (filterModel) {
      const isAnyIncompleteFilter = filterModel?.items?.some((filter) => !filter?.value);

      if (!isAnyIncompleteFilter) {
        getPoliciesData();
      }
    } else {
      getPoliciesData();
    }
  }, [getPoliciesData, filterModel]);

  const agriculturePoliciesColumns = [
    {
      field: "policy.referenceDate",
      headerName: t("agriculture.year"),
      valueGetter: (params) => params.row.policy.referenceDate,
    },
    {
      field: "policy.seasonDescription",
      headerName: t("agriculture.season"),
      valueGetter: (params) => params.row.policy.seasonDescription,
    },

    {
      field: "complainID",
      headerName: t("agriculture.complainId"),
    },

    {
      field: "bulletinID",
      headerName: t("agriculture.bulletinId"),
    },

    {
      field: "policy.policyNumber",
      headerName: t("agriculture.policyNo"),
      valueGetter: (params) => params.row.policy.policyNumber,
    },
    {
      field: "policy.companyCode",
      headerName: t("company"),
      type: "singleSelect",
      filterOperators: isSingleSelectOperators,
      valueOptions: getCompanyCodeFilter(config.agriCompanyCodesMap),
      valueGetter: (params) => getCompanyCodeName(params.row.policy.companyCode, config.agriCompanyCodesMap),
      sortable: false,
    },
    {
      field: "policy.agencyCode",
      headerName: t("agencyCode"),
      valueGetter: (params) => params.row.policy.agencyCode,
      sortable: false,
    },
    {
      field: "policy.agencyDescription",
      headerName: t("agency"),
      valueGetter: (params) => params.row.policy.agencyDescription,
    },
    {
      field: "policy.subagencyCode",
      headerName: t("subagencyCode"),
      valueGetter: (params) => params.row.policy.subagencyCode,
      sortable: false,
    },
    {
      field: "policy.addressCity",
      headerName: t("agriculture.municipality"),
      valueGetter: (params) => params.row.policy.addressCity,
      sortable: false,
    },
    {
      field: "policy.addressProvince",
      headerName: t("agriculture.province"),
      valueGetter: (params) => params.row.policy.addressProvince,
    },
    {
      field: "policy.plantSpecies",
      headerName: t("agriculture.specie"),
      valueGetter: (params) => params.row.policy.plantSpecies,
    },
    {
      field: "policy.plantMacropecies",
      headerName: t("agriculture.macrospecie"),
      valueGetter: (params) => params.row.policy.plantMacropecies,
    },
    {
      field: "adversityDescription",
      headerName: t("agriculture.adversity"),
    },
    {
      field: "damageDate",
      headerName: t("agriculture.damageDate"),
      type: "date",
      filterOperators: dateFilterOperators,
      valueGetter: (params) => params.row.damageDate && formatDateValue(params.row.damageDate),
    },
    // {
    //   field: "periziaDate",
    //   headerName: t("agriculture.assessmentDate"),
    //   type: "date",
    //   filterOperators: dateFilterOperators,
    //   valueGetter: (params) =>
    //     params.row.periziaDate && formatDateValue(params.row.periziaDate),
    // }
  ];

  const columns = agriculturePoliciesColumns.map((tableColumn) => ({
    valueFormatter: (params) => tableCellRender(params.value?.toString()),
    filterOperators: stringFilterOperators,
    flex: 1,
    ...tableColumn,
  }));

  return (
    <Grid style={{ marginTop: 25 }}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12}>
          <Box>
            <Card>
              <UniversalMap
                page={"agriculture-claims"}
                filteredMapDataProp={filteredMapData}
                setFilteredMapDataProp={setFilteredMapData}
                routeNameProp="agricultureClaims"
              />
            </Card>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12}>
          <div style={{ height: 700, width: "100%" }}>
            <DataGridCystellarTable
              columns={columns}
              rows={agricultureClaimsData}
              rowCount={tableState.total}
              getRowId={(rowData) => rowData._id}
              onRowClick={onTableRowClick}
              loading={tableState.isLoading}
              paginationMode={"server"}
              paginationModel={paginationModel}
              onPaginationModelChange={(newPaginationModel) => setPaginationModel(newPaginationModel)}
              filterMode={"server"}
              onFilterModelChange={onFilterChange}
              sortingMode={"server"}
              onSortModelChange={onSortChange}
              customToolbarComp={lastUpdateDateMsg ? true : false}
              customToolbarCompMsg={lastUpdateDateMsg}
              customToolbarWidgets={
                <UpdateMapButton
                  setFilteredMapData={setFilteredMapData}
                  filterModel={filterModel}
                  routeName="agricultureClaims"
                />
              }
            />
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};
